<script lang="ts">
	import { format } from 'svelte-i18n';
	import { user_data } from 'src/stores/account/user';
	import {
		Button,
		Modal,
		Text,
		Container,
		ButtonGroup,
		Input,
		Row,
		Col,
		Select,
	} from '@Shelly/shelly-design-system';
	import { COUNTRIES, COUNTRIES_WITH_VAT_EU } from 'src/constants';
	import { paypal, paypalTest } from 'src/services/http';
	import { getContext } from 'svelte';
	import { capFirstChar } from 'src/helpers/utilities';
	import AcceptTermsAndAge from '../AcceptTermsAndAge.svelte';

	import { isDevServerUser } from 'src/helpers/user_server_conditions';

	import InvoiceEuDetails from './InvoiceEUDetails.svelte';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';

	export let showModal = false;
	export let callback = () => {};
	export let initial = false;
	export let title = $format('invoice_details', { default: 'Invoice Details' });

	let invoiceDetails: SvelteStore<any> = getContext('invoice_details');
	let { type, name, vat, country, address } = $invoiceDetails;
	$: vat = vat?.replace(/\s/g, '');
	type = Object.keys($invoiceDetails).length > 0 ? $invoiceDetails.type : 'personal';

	let invoiceEUDetails;
	let accepted = false;
	let onError = false;

	let value = {
		title: COUNTRIES.find((c) => {
			if (country === 'GR') {
				return c.CountryCode === 'EL';
			}
			return c.CountryCode === country;
		})?.['CountryName'],
		value: country === 'GR' ? 'EL' : country,
	};

	const allCountriesOptions = COUNTRIES.map((e) => {
		return {
			title: e.CountryName,
			value: e.CountryCode,
		};
	});

	const euCountriesOptions = COUNTRIES_WITH_VAT_EU.map((e) => {
		return {
			title: e.CountryName,
			value: e.CountryCode,
		};
	});

	$: isPremium = $user_data.premium;

	$: isInEU = euCountriesOptions.find((country) => {
		if (country.title === value?.title) {
			return true;
		}
		return false;
	});

	$: disableSaveButton =
		type === 'personal'
			? (initial && !accepted) || !value?.value
			: isInEU
				? onError || (initial && !accepted)
				: !(value?.value && vat && name && address) || (initial && !accepted);

	const handleCountryChange = (e) => {
		value = allCountriesOptions.find((country) => {
			return country.title === e.detail;
		});

		name = '';
		address = '';
		vat = '';
	};

	const updateHomeData = async () => {
		let billingDetails: any = {};

		if (isInEU && type === 'business') {
			billingDetails = {
				...invoiceEUDetails.getBillingDetails(),
				country: value.value,
				type,
			};
		} else if (type === 'personal') {
			billingDetails = { type, country: value.value };
		} else {
			billingDetails = {
				type,
				name: name,
				vat: vat,
				country: value.value,
				address: address,
			};
		}

		if (billingDetails.country === 'EL') {
			billingDetails.country = 'GR';
		}

		try {
			let res;
			if (isDevServerUser(['repo', 't1', 'nb'])) {
				res = await paypalTest.put('/api/invoice_data', billingDetails);
			} else {
				res = await paypal.put('/api/invoice_data', billingDetails);
			}

			$invoiceDetails = billingDetails;
		} catch (error) {
			console.error(error);
		}

		if (!isPremium || initial) {
			callback();
		}
		showModal = false;
	};
</script>

<Modal on:close={() => (showModal = false)} {showModal} {title} backdropClose removeScrollPadding>
	<svelte:fragment slot="content">
		<Container padding="scrollPaddings">
			<div class="d-flex align-items-center flex-wrap mb-s">
				<Text class="mr-s mb-s" styleType="h2">
					{$format('text_premium_invoice_receiver_type', {
						default: 'Invoice Receiver Type',
					})}
				</Text>
				<ButtonGroup
					id="buttongroup-d8g8fhj"
					class="mb-s"
					aria-label={$format('label_invoice_details_modal_type', {
						default: 'Invoice details type',
					})}
				>
					<Button
						class={type === 'personal' && 'active'}
						size="m"
						on:click={() => (type = 'personal')}
					>
						{$format('button_invoice_details_modal_personal', {
							default: 'Personal',
						})}
					</Button>
					<Button
						class={type === 'business' && 'active'}
						size="m"
						on:click={() => (type = 'business')}
					>
						{$format('button_invoice_details_modal_business', {
							default: 'Business',
						})}
					</Button>
				</ButtonGroup>
			</div>

			<Row>
				<Col xs="12" md="3" class="width-full">
					<Select
						aria-labelledby="select-ir20sle"
						isAutocomplete
						size="m"
						label={capFirstChar(
							$format('select_label_invoice_details_modal_country', {
								default: 'Country',
							}),
						)}
						icon="fas fa-globe"
						options={allCountriesOptions.map((country) => country.title)}
						value={value.title}
						on:change={(e) => handleCountryChange(e)}
						id="select-ir20sle"
					/>
					<div
						class={classMap({
							'mb-s': type === 'personal',
							'mb-m': type === 'business',
						})}
					/>
				</Col>
			</Row>
			{#if type === 'business'}
				{#if isInEU}
					<InvoiceEuDetails
						bind:this={invoiceEUDetails}
						selectedCountry={value}
						bind:onError
						bind:name
						bind:vat
						bind:address
					></InvoiceEuDetails>
				{:else}
					<Input
						class="mb-m"
						icon="fas fa-file-invoice"
						label={$format('premium_invoice_details_vat', {
							default: 'VAT',
						})}
						bind:value={vat}
						id="input-mqn9usg"
						aria-labelledby="input-mqn9usg"
					/>
					<Input
						class="mb-m"
						icon="fas fa-building"
						label={$format('premium_invoice_details_company_name', {
							default: 'Company name',
						})}
						bind:value={name}
						id="input-msvmemx"
						aria-labelledby="input-msvmemx"
					/>
					<Input
						class="mb-m"
						icon="fas fa-home"
						label={$format('premium_invoice_details_address', {
							default: 'Address name',
						})}
						bind:value={address}
						id="input-b4ipwsz"
						aria-labelledby="input-b4ipwsz"
					/>
				{/if}
			{/if}
		</Container>
		{#if initial}
			<AcceptTermsAndAge bind:accepted />
		{/if}
	</svelte:fragment>
	<div slot="footer" class="d-flex justify-content-end">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{
				sm: 'm',
			}}
			variant="secondary"
			on:click={() => (showModal = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</Button>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			disabled={disableSaveButton}
			responsiveSize={{
				sm: 'm',
			}}
			on:click={updateHomeData}
		>
			{initial
				? capFirstChar(
						$format('button_invoice_details_modal_save_pay', {
							default: 'save and pay',
						}),
					)
				: $format('button_save', { default: 'Save' })}
		</Button>
	</div>
</Modal>
